import {Controller, FieldArrayWithId, useFormContext} from 'react-hook-form'
import {DiseaseExtended, TreatmentRequestDiagnosisValidationSchema} from '@/features/treatment-requests/types'
import {Grid} from '@/components/ui/grid-atoms/GridAtoms'
import {InputSelectDisease} from '@/features/treatment-requests/components/input-select-disease/InputSelectDisease'
import {useAsync} from '@/hooks/useAsync'
import {httpGetDisease} from '@/features/treatment-requests/services/treatment-requests.http'
import {InputHelpText} from '@/components/ui/input-help-text/InputHelpText'
import {DiseasesGroupsSelectorLoader} from '@/features/treatment-requests/components/diseases-groups-selectors-loader/DiseasesGroupsSelectorsLoader'
import {useTranslation} from '@/translations/i18n'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {StyledIdentationDivider, StyledIndentedGridItem} from './style'
import {PatientAgeRange} from '@/types'
import {Fragment, ReactNode, useEffect} from 'react'
import {makeDiseaseTreePathFromArray} from '@/features/treatment-requests/utils'

interface DiseasesGroupsSelectors {
    fields: FieldArrayWithId<
        {
            diseases?: DiseaseExtended[]
        },
        'diseases',
        'key'
    >[]
    serviceType: Exclude<PatientAgeRange, 'evolutionary_age_adults'>
    onChange: (value: DiseaseExtended) => void
    onClear: (index: number) => void
}

interface RenderInputSelectProps {
    diseaseId?: string
    defaultValue?: DiseaseExtended
    label: string
    placeholder: string
    serviceType: Exclude<PatientAgeRange, 'evolutionary_age_adults'>
    onChange: (value: DiseaseExtended['id'] | null) => void
    onClear?: () => void
}

export const DiseasesGroupsSelectors = ({fields, serviceType, onChange, onClear}: DiseasesGroupsSelectors) => {
    const {t} = useTranslation()
    const form = useFormContext<TreatmentRequestDiagnosisValidationSchema>()

    const selectedGroupDetailsQuery = useAsync()

    const onSelect = async (diseaseId?: DiseaseExtended['id'] | null) => {
        if (!diseaseId) {
            return
        }

        try {
            const {data} = await selectedGroupDetailsQuery.run(
                httpGetDisease({
                    urlParams: {diseaseId},
                    params: {responseType: 'extended'}
                })
            )

            onChange(data)
        } catch (error) {
            console.error(error)
        }
    }

    useEffect(() => {
        if (fields.length > 0) {
            onSelect(fields[fields.length - 1].id)
        }
    }, [])

    const renderNestedStructure = (diseases: DiseaseExtended[], iteration: number = 0): ReactNode => {
        const renderInputSelect = (props: RenderInputSelectProps) => <InputSelectDisease isOptional {...props} />

        const renderController = (iteration: number, inputSelectProps: Omit<RenderInputSelectProps, 'diseaseId'>) => (
            <Controller
                control={form.control}
                name={`diseases.${iteration}`}
                render={({field}) =>
                    renderInputSelect({
                        ...inputSelectProps,
                        diseaseId: field.value.id
                    })
                }
            />
        )

        const renderIndentedItem = ({content, isFirstRow = false}: {content: ReactNode; isFirstRow: boolean}) => (
            <StyledIndentedGridItem fullWidth gap={2} spanColumn={4} $isFirstRow={isFirstRow} direction="column">
                {!isFirstRow && <StyledIdentationDivider />}
                {content}
            </StyledIndentedGridItem>
        )

        return diseases.map(disease => {
            const diseaseSubGroups = fields.find(field => field.id == disease.id)?.children

            if (iteration == 0) {
                return (
                    <Fragment key={disease.id}>
                        {iteration == 0 && disease.children && renderNestedStructure(disease.children, iteration + 1)}
                        {!disease.parent &&
                            diseaseSubGroups &&
                            diseaseSubGroups?.length > 0 &&
                            renderIndentedItem({
                                isFirstRow: true,
                                content: renderController(iteration, {
                                    label: t('treatment_requests:diseases_groups_label'),
                                    placeholder: t('treatment_requests:select_diseases_groups_placeholder'),
                                    serviceType: serviceType,
                                    onChange: value => onSelect(value)
                                })
                            })}
                    </Fragment>
                )
            }

            return (
                <Fragment key={disease.id}>
                    {diseases.map((disease, index) =>
                        renderIndentedItem({
                            isFirstRow: iteration <= 1,
                            content: (
                                <Fragment key={`${disease.id}-${index}`}>
                                    {renderInputSelect({
                                        label: t('treatment_requests:diseases_groups_label'),
                                        placeholder: t('treatment_requests:select_diseases_groups_placeholder'),
                                        serviceType,
                                        diseaseId: disease.parent?.id,
                                        onChange: value => onSelect(value),
                                        defaultValue: disease,
                                        onClear: () => {
                                            (onClear(iteration), onSelect(disease.parent?.id))
                                        }
                                    })}

                                    {disease.children && renderNestedStructure(disease.children, iteration + 1)}

                                    {diseaseSubGroups &&
                                        diseaseSubGroups?.length > 0 &&
                                        renderIndentedItem({
                                            isFirstRow: false,
                                            content: renderController(iteration, {
                                                label: t('treatment_requests:diseases_groups_label'),
                                                placeholder: t('treatment_requests:select_diseases_groups_placeholder'),
                                                serviceType: serviceType,
                                                onChange: value => onSelect(value)
                                            })
                                        })}
                                </Fragment>
                            )
                        })
                    )}
                </Fragment>
            )
        })
    }

    return (
        <Flexbox direction="column" gap={4} fullWidth>
            <Grid columns={6} gap={3} fullWidth style={{position: 'relative'}}>
                {renderNestedStructure(makeDiseaseTreePathFromArray(fields))}
            </Grid>
            {selectedGroupDetailsQuery.isError && (
                <InputHelpText error={t('treatment_requests:diseases_groups_error')} />
            )}
            {selectedGroupDetailsQuery.isLoading && <DiseasesGroupsSelectorLoader />}
        </Flexbox>
    )
}
